/**
 * Page-builder visibility maps onto Bootstrap 4 display utilities,
 * aligned with the GrapesJS device manager:
 * Mobile < md, Tablet md–lg, Laptop lg–xl, Desktop ≥ xl.
 */

export const VISIBILITY_DEVICES = [
  {
    id: 'desktop',
    bp: 'xl',
    icon: 'fa-desktop',
    i18nKey: 'Desktop',
  },
  {
    id: 'laptop',
    bp: 'lg',
    icon: 'fa-laptop',
    i18nKey: 'Laptop',
  },
  {
    id: 'tablet',
    bp: 'md',
    icon: 'fa-tablet',
    i18nKey: 'Tablet',
  },
  {
    id: 'mobile',
    bp: null,
    icon: 'fa-mobile',
    i18nKey: 'Mobile',
  },
]

export const VISIBILITY_STEPS = [...VISIBILITY_DEVICES].reverse()

export const VISIBILITY_CLASS_NAMES = [
  'd-none',
  'd-block',
  'd-md-none',
  'd-md-block',
  'd-lg-none',
  'd-lg-block',
  'd-xl-none',
  'd-xl-block',
]

/**
 * @param {string|null} bp
 * @param {boolean} visible
 *
 * @returns {string}
 */
const displayClass = (bp, visible) => {
  const display = visible ? 'block' : 'none'
  return bp ? `d-${bp}-${display}` : `d-${display}`
}

/**
 * @param {string[]} visibleIds
 *
 * @returns {string[]}
 */
export const visibilityToClasses = visibleIds => {
  const visible = new Set(visibleIds)

  if (visible.size === VISIBILITY_STEPS.length) {
    return []
  }

  const classes = []
  let shown = null

  VISIBILITY_STEPS.forEach(({ id, bp }) => {
    const want = visible.has(id)

    if (shown === want) {
      return
    }

    shown = want
    classes.push(displayClass(bp, want))
  })

  return classes
}

/**
 * @param {string[]} classes
 *
 * @returns {string[]}
 */
export const classesToVisibility = classes => {
  const set = new Set(Array.isArray(classes) ? classes : [])
  const hasControlled = VISIBILITY_CLASS_NAMES.some(name => set.has(name))

  if (!hasControlled) {
    return VISIBILITY_STEPS.map(step => step.id)
  }

  let shown = true
  const visible = []

  VISIBILITY_STEPS.forEach(({ id, bp }) => {
    const noneClass = displayClass(bp, false)
    const blockClass = displayClass(bp, true)

    if (set.has(noneClass)) {
      shown = false
    } else if (set.has(blockClass)) {
      shown = true
    }

    if (shown) {
      visible.push(id)
    }
  })

  return visible
}

/**
 * @param {string[]} currentClasses
 * @param {string[]} visibleIds
 *
 * @returns {string[]}
 */
export const applyVisibilityClasses = (currentClasses, visibleIds) => {
  const kept = (currentClasses || []).filter(name => !VISIBILITY_CLASS_NAMES.includes(name))
  return [...kept, ...visibilityToClasses(visibleIds)]
}
